import { Component, Input } from '@angular/core';
import { dayjs } from '@karve.it/core';

import { Store, createSelector } from '@ngrx/store';

import { ReplaySubject, distinctUntilChanged, switchMap } from 'rxjs';

import { InvoiceWithArtifactsFragment } from '../../../generated/graphql.generated';
import { FreyaCommonModule } from '../../freya-common/freya-common.module';
import { isDraftInvoice, isFinalizedInvoice } from '../../invoices/invoices.utils';
import { remainingBalance } from '../../jobs/jobs.util';
import { formatCurrency } from '../../lib.ts/currency.util';
import { FreyaHelperService } from '../../services/freya-helper.service';
import { SharedModule } from "../../shared/shared.module";
import { createCheckSinglePermissionSelector } from '../../state/branding.store';
import { DEFAULT_DISABLED_TOOLTIPS as DEFAULT_TOOLTIPS} from '../../utilities/menu-item.util';
import { jobToolFeature, selectCustomer, selectInvoicesDictionary } from '../job-tool.reducer';

import { JobInvoiceActions } from './job-invoice.actions';

export type TagInfo = {
  text: string;
  color: string;
  backgroundColor: string;
}

@Component({
    selector: 'app-job-invoice',
    standalone: true,
    templateUrl: './job-invoice.component.html',
    styleUrls: [
        '../job-financials/document-style.scss',
        './job-invoice.component.scss',
    ],
    imports: [
        FreyaCommonModule,
        SharedModule
    ]
})
export class JobInvoiceComponent {

  @Input() set invoiceId(value: string) {
    this._invoiceId$.next(value);
  };

  private _invoiceId$ = new ReplaySubject<string>(1);

  private _selectInvoiceById = (invoiceId: string) => createSelector(
    selectInvoicesDictionary,
    (invoices) => invoices[invoiceId],
  );

  private _selectSendButtonDisabledStatus = (invoiceId: string) => createSelector(
    selectInvoicesDictionary,
    selectCustomer,
    jobToolFeature.selectDocumentsSending,
    jobToolFeature.selectDocumentsUpdating,
    (invoices, customer, documentsSending, documentsUpdating) => {

      const invoice = invoices[invoiceId];
      const isGenerating = this._isGenerating(invoice);
      const isVoided = Boolean(invoice.voidedAt);
      const isDeleted = Boolean(invoice.deletedAt);
      const contactInfoMissing = !customer?.email && !customer?.phone;
      const isSending = invoice.id in documentsSending;
      const isUpdating = invoice.id in documentsUpdating;

      const conditions = [
        { condition: isGenerating, tooltip: DEFAULT_TOOLTIPS.documentGenerating },
        { condition: isVoided, tooltip: DEFAULT_TOOLTIPS.invoiceVoided },
        { condition: isDeleted, tooltip: 'This invoice has been deleted' },
        { condition: isSending, tooltip: 'Sending' },
        { condition: isUpdating, tooltip: 'Updating' },
        { condition: contactInfoMissing, tooltip: 'No customer email or phone on file' },
      ];

      const firstMatchingCondition = conditions.find(({ condition }) => condition);

      return {
        disabled: Boolean(firstMatchingCondition),
        disabledTooltip: firstMatchingCondition?.tooltip || '',
      }
    },
  );

  private _selectTags = (invoiceId: string) => createSelector(
    selectInvoicesDictionary,
    (invoices) => {
      const invoice = invoices[invoiceId];
      const isGenerating = this._isGenerating(invoice);
      const isVoided = Boolean(invoice.voidedAt);
      const isDeleted = Boolean(invoice.deletedAt);
      const isSent = Boolean(invoice.sentAt);
      const isOpened = Boolean(invoice.openedAt);

      const tags: TagInfo[] = []; 

      if (isGenerating && !isDeleted) {
        tags.push({
          text: 'Generating',
          ...this.freyaHelper.getTagColors('warning'),
        });
        return tags;
      }

      if (isSent) {
        tags.push({
          text: 'Sent',
          ...this.freyaHelper.getTagColors('success'),
        });
      } else {
        tags.push({
          text: 'Not Sent',
          ...this.freyaHelper.getTagColors('neutral'),
        });
      }

      if (isOpened) {
        tags.push({
          text: 'Opened',
          ...this.freyaHelper.getTagColors('success'),
        });
      }

      // Per-role signing badges. Engine-agnostic: both engines stamp
      // signedByCustomer/signedByEmployee on the artifact metadata when a role
      // signs (PandaDoc in pandadoc.helpers, Aegis in aegis.signDocument), so
      // this surfaces signatures regardless of engine. PandaDoc invoices were
      // never signable, so in practice these light up for Aegis invoices whose
      // template declares signer roles. Mirrors the job-estimate badges.
      const document = invoice?.artifacts?.[0];

      if (document?.metadata?.signedByCustomer) {
        tags.push({
          text: 'Signed by Customer',
          ...this.freyaHelper.getTagColors('success'),
        });
      }

      if (document?.metadata?.signedByEmployee) {
        tags.push({
          text: 'Signed by Employee',
          ...this.freyaHelper.getTagColors('neutral'),
        });
      }

      if (isVoided) {
        tags.push({
          text: 'Voided',
          ...this.freyaHelper.getTagColors('danger'),
        });
        return tags;
      }

      if (isDeleted) {
        tags.push({
          text: 'Deleted',
          ...this.freyaHelper.getTagColors('danger'),
        });
        return tags;
      }

      if (remainingBalance(invoice) > 0) {

        tags.push({
          text: 'Not paid',
          ...this.freyaHelper.getTagColors('warning'),
        });

        const dueDate = dayjs.unix(invoice.dueDate);

        tags.push({
          text: `Due on ${dueDate.format('MMM D, YYYY')}`,
          ...this.freyaHelper.getTagColors('neutral'),
        });
      } else {
        tags.push({
          text: 'Paid',
          ...this.freyaHelper.getTagColors('success'),
        });
      }

      return tags;
    }
  );

  private _selectActionsMenu = createSelector(
    jobToolFeature.selectActionsMenuInvoiceId,
    selectInvoicesDictionary,
    createCheckSinglePermissionSelector({ permission: 'invoice.update' }),
    (invoiceId, invoices, canUpdate) => {

      const invoice = invoices[invoiceId];

      if (!invoice) { return []; }

      const invoiceVoided = Boolean(invoice.voidedAt);
      const invoiceDeleted = Boolean(invoice.deletedAt);
      const invoicePaid = remainingBalance(invoice) === 0;
      const documentGenerating = this._isGenerating(invoice);
      const invoiceFinalized = isFinalizedInvoice(invoice);
      const invoiceDraft = isDraftInvoice(invoice);
      const invoiceExportedToQuickbooks = Boolean(invoice?.metadata?.quickbooksId);

      return [
        {
          id: 'pay',
          label: 'Pay',
          icon: 'pi pi-wallet',
          command: () => this.store.dispatch(this.actions.payInvoiceMenuItemClicked()),
            ...this._getMenuItemDisabledStatus({
            invoiceVoided,
            invoiceDeleted,
            invoicePaid,
          }),
        },
        {
          id: 'preview',
          label: 'Preview',
          icon: 'pi pi-eye',
          command: () => this.store.dispatch(this.actions.previewInvoiceMenuItemClicked()),
            ...this._getMenuItemDisabledStatus({
            documentGenerating,
          }),
        },
        {
          id: 'download',
          label: 'Download',
          icon: 'pi pi-download',
          command: () => this.store.dispatch(this.actions.downloadInvoiceMenuItemClicked()),
            ...this._getMenuItemDisabledStatus({
            documentGenerating,
            invoiceDeleted,
          }),
        },
        {
          id: 'employee',
          label: 'View as Employee',
          icon: 'pi pi-external-link',
          command: () => this.store.dispatch(this.actions.viewInvoiceAsEmployeeMenuItemClicked()),
          visible: canUpdate,
            disabled: true,
          ...this._getMenuItemDisabledStatus({
            documentGenerating,
            invoiceVoided,
            invoiceDeleted,
          }),
        },
        {
          id: 'finalize',
          label: 'Finalize',
          icon: 'pi pi-check',
          command: () => this.store.dispatch(this.actions.finalizeInvoiceMenuItemClicked()),
          visible: canUpdate,
            disabled: true,
          ...this._getMenuItemDisabledStatus({
            documentGenerating,
            invoiceFinalized,
            invoiceVoided,
            invoiceDeleted,
          }),
        },
        {
          id: 'customer',
          label: 'View as Customer',
          icon: 'pi pi-external-link',
          command: () => this.store.dispatch(this.actions.viewInvoiceAsCustomerMenuItemClicked()),
          visible: canUpdate,
            ...this._getMenuItemDisabledStatus({
            documentGenerating,
            invoiceVoided,
            invoiceDeleted,
          }),
        },
        {
          id: 'void',
          label: 'Void',
          icon: 'pi pi-times',
          command: () => this.store.dispatch(this.actions.voidInvoiceMenuItemClicked()),
            ...this._getMenuItemDisabledStatus({
            invoiceVoided,
            invoiceDraft,
            invoiceDeleted,
          }),
          // visible: false,
        },
        {
          id: 'delete',
          label: 'Delete',
          icon: 'pi pi-trash',
          command: () => this.store.dispatch(this.actions.deleteInvoiceMenuItemClicked()),
            ...this._getMenuItemDisabledStatus({
            invoiceDeleted,
            invoiceVoided,
            invoiceFinalized,
          }),
          // visible: false,
        },
        {
          id: 'export-to-quickbooks',
          label: 'Export to Quickbooks',
          icon: 'pi pi-share-alt',
          command: () => this.store.dispatch(this.actions.exportInvoiceToQuickbooksMenuItemClicked()),
            // visible: false,
            ...this._getMenuItemDisabledStatus({
            invoiceExportedToQuickbooks,
            invoiceVoided,
            invoiceDraft,
            documentGenerating,
            invoiceDeleted,
          }),
        },
        {
          id: 'force-update',
          label: 'Force Update Invoice',
          icon: 'pi pi-sync',
          command: () => this.store.dispatch(this.actions.forceUpdateInvoiceMenuItemClicked()),
            disabled: true,
          ...this._getMenuItemDisabledStatus({
            documentGenerating,
            invoiceVoided,
            invoiceDeleted,
          }),
        },
        {
          id: 'open-in-right-panel',
          label: 'Open in Right Panel',
          icon: 'pi pi-external-link',
          command: () => this.store.dispatch(this.actions.openInvoiceInRightPanelMenuItemClicked()),
            disabled: false,
        },
      ];
    }
  );

  private _selectSendButtonLabel = (invoiceId: string) => createSelector(
    selectInvoicesDictionary,
    jobToolFeature.selectDocumentsSending,
    jobToolFeature.selectDocumentsUpdating,
    (invoices, documentsSending, documentsUpdating) => {

      const invoice = invoices[invoiceId];
      const isSending = invoice.id in documentsSending;
      const isUpdating = invoice.id in documentsUpdating;
      const isSent = Boolean(invoice.sentAt);

      if (isSending) {
        return 'Sending';
      }

      if (isUpdating) {
        return 'Updating';
      }

      return isSent ? 'Resend' : 'Send';
    }
  );

  public viewModel$ = this._invoiceId$.pipe(
    distinctUntilChanged(),
    switchMap((invoiceId) => {

      const selectSendButtonDisabledStatus = this._selectSendButtonDisabledStatus(invoiceId);

      return this.store.select(createSelector({
        invoice: this._selectInvoiceById(invoiceId),
        name: createSelector(
          this._selectInvoiceById(invoiceId),
          (invoice) => `Invoice #${invoice.invoiceNumber}`,
        ),
        balance: createSelector(
          this._selectInvoiceById(invoiceId),
          (invoice) => formatCurrency((invoice.total - invoice.paidTotal) / 100, invoice.currency),
        ),
        actionsMenu: this._selectActionsMenu,
        tags: this._selectTags(invoiceId),
        sendButtonLabel: this._selectSendButtonLabel(invoiceId),
        sendButtonIcon: createSelector(
          jobToolFeature.selectDocumentsSending,
          jobToolFeature.selectDocumentsUpdating,
          (documentsSending, documentsUpdating) => (invoiceId in documentsSending || invoiceId in documentsUpdating)
            ? 'pi pi-spinner pi-spin'
            : 'pi pi-send',
        ),
        sendButtonDisabled: createSelector(
          selectSendButtonDisabledStatus,
          ({ disabled }) => disabled,
        ),
        sendButtonDisabledTooltip: createSelector(
          selectSendButtonDisabledStatus,
          ({ disabledTooltip }) => disabledTooltip,
        ),
      }))
    }),
  );

  public actions = JobInvoiceActions;

  constructor(
    public store: Store,
    private freyaHelper: FreyaHelperService,
  ) {}

  private _isGenerating(invoice: InvoiceWithArtifactsFragment) {
    const document = invoice?.artifacts?.[0];
    return !(document?.url || document?.signedUrl);
  }

  _getMenuItemDisabledStatus(input: {
    documentGenerating?: boolean;
    invoiceVoided?: boolean;
    invoicePaid?: boolean;
    invoiceDraft?: boolean;
    invoiceDeleted?: boolean;
    invoiceFinalized?: boolean;
    invoiceExportedToQuickbooks?: boolean;
    hasPlaceholderEmail?: boolean;
  }) {

    const labels: Record<keyof typeof input, string> = {
      documentGenerating: 'Document is generating',
      invoiceVoided: 'Invoice is voided',
      invoicePaid: 'Invoice is paid',
      invoiceDraft: 'Invoice is a draft',
      invoiceDeleted: 'Invoice is deleted',
      invoiceFinalized: 'Invoice is finalized',
      invoiceExportedToQuickbooks: 'Invoice is exported to Quickbooks',
      hasPlaceholderEmail: 'There is no email associated with this invoice',
    };

    for (const key of Object.keys(input)) {
      if (input[key]) {
        return {
          disabled: true,
          tooltipOptions: {
            tooltipPosition: 'left' as const,
            tooltipLabel: labels[key],
          },
        };
      }
    }

    return {
      disabled: false,
    }
  }

}
